/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { IResourceId, IResourceMap } from 'apmui/resource/ResourceLoader';
import { RESOURCE, RESOURCE_TO_SECTION } from 'apmui/resource/ResourceType';
import { WEBTOP_SECTION_SORT, WEBTOP_SECTION_STATE } from './Model';
import WebtopResourceModel from 'apmui/resource/WebtopResourceModel';
import Object from 'apmui/util/Object';
import deepCopy from 'apmui/util/deepCopy';

/**
 * Way of assignment of resources to section
 * IDs for default webtop sections.
 * Captions for them are configurable and defined in Model/WebtopCaptions.
 */
enum DEFAULT_SECTION_ID {
    FALLBACK = '#fallback',
    LEGACY_NA = '#legacy_na',
    LEGACY_OTHER = '#legacy_other',
}
const LEGACY_CONFIG = {
    [RESOURCE.WEBTOP_SECTION]: {
        param: '',
        resources: {
            [DEFAULT_SECTION_ID.LEGACY_OTHER]: {
                props: {
                    id: DEFAULT_SECTION_ID.LEGACY_OTHER,
                    type: RESOURCE.WEBTOP_SECTION,
                    description: 'Default Resources section',
                    caption: 'Resources',
                    custom_img_path: WebtopResourceModel.getDefaultIcon(RESOURCE.WEBTOP_SECTION),
                    initial_state: String(WEBTOP_SECTION_STATE.EXPANDED),
                    sort: String(WEBTOP_SECTION_SORT.ALPHABETICALLY),
                    ws_order: '0',
                },
                assignedResources: [] as IResourceId[],
            },
            [DEFAULT_SECTION_ID.LEGACY_NA]: {
                props: {
                    id: DEFAULT_SECTION_ID.LEGACY_NA,
                    type: RESOURCE.WEBTOP_SECTION,
                    description: 'Default Network Access section',
                    caption: 'Network Access',
                    custom_img_path: WebtopResourceModel.getDefaultIcon(RESOURCE.WEBTOP_SECTION),
                    initial_state: String(WEBTOP_SECTION_STATE.EXPANDED),
                    sort: String(WEBTOP_SECTION_SORT.ALPHABETICALLY),
                    ws_order: '1',
                },
                assignedResources: [] as IResourceId[],
            },
        },
    },
};

const FALLBACK_CONFIG = {
    [RESOURCE.WEBTOP_SECTION]: {
        param: '',
        resources: {
            [DEFAULT_SECTION_ID.FALLBACK]: {
                props: {
                    id: DEFAULT_SECTION_ID.FALLBACK,
                    type: RESOURCE.WEBTOP_SECTION,
                    description: 'Default Network Access section',
                    caption: 'Network Access',
                    custom_img_path: WebtopResourceModel.getDefaultIcon(RESOURCE.WEBTOP_SECTION),
                    initial_state: '1',
                    sort: String(WEBTOP_SECTION_SORT.ALPHABETICALLY),
                    ws_order: Number.POSITIVE_INFINITY.toString(),
                },
                assignedResources: [] as IResourceId[],
            },
        },
    },
};

/**
 * Get default sections required for given initial resource list.
 *
 * Two possible scenarios are available: no webtop sections received at all or some resources are not assigned into
 * received webtop sections. In first case we gonna create Network Access Section and Other Resources Section
 * in other we gonna create fallback section
 *
 * @param resourceMap
 * @param sectionConfig
 */
function createDefaultSections(
    resourceMap: IResourceMap,
    localizedCaptions: Record<DEFAULT_SECTION_ID, string>
): IResourceMap | undefined {
    const sections =
        resourceMap[RESOURCE.WEBTOP_SECTION] && resourceMap[RESOURCE.WEBTOP_SECTION]!.resources
            ? Object.values(resourceMap[RESOURCE.WEBTOP_SECTION]!.resources)
            : [];
    const resourceGroups = Object.entries(resourceMap) as Array<[RESOURCE, IResourceMap[RESOURCE]]>;

    if (sections.length === 0) {
        const config = deepCopy(LEGACY_CONFIG);
        const legacyNa = config[RESOURCE.WEBTOP_SECTION].resources[DEFAULT_SECTION_ID.LEGACY_NA];
        if (localizedCaptions[DEFAULT_SECTION_ID.LEGACY_NA]) {
            legacyNa.props.caption = localizedCaptions[DEFAULT_SECTION_ID.LEGACY_NA];
        }
        const legacyLinkApp = config[RESOURCE.WEBTOP_SECTION].resources[DEFAULT_SECTION_ID.LEGACY_OTHER];
        if (localizedCaptions[DEFAULT_SECTION_ID.LEGACY_OTHER]) {
            legacyLinkApp.props.caption = localizedCaptions[DEFAULT_SECTION_ID.LEGACY_OTHER];
        }

        for (const [type, resourceGroup] of resourceGroups) {
            if (resourceGroup && resourceGroup.resources && RESOURCE_TO_SECTION.indexOf(type) !== -1) {
                for (const id of Object.keys(resourceGroup.resources)) {
                    if (type === RESOURCE.NETWORK_ACCESS) {
                        legacyNa.assignedResources.push({ type, id });
                    } else {
                        legacyLinkApp.assignedResources.push({ type, id });
                    }
                }
            }
        }
        return config;
    } else {
        const config = deepCopy(FALLBACK_CONFIG);
        const fallback = config[RESOURCE.WEBTOP_SECTION].resources[DEFAULT_SECTION_ID.FALLBACK];
        fallback.assignedResources = [];
        if (localizedCaptions[DEFAULT_SECTION_ID.FALLBACK]) {
            fallback.props.caption = localizedCaptions[DEFAULT_SECTION_ID.FALLBACK];
        }
        // Remove all assigned to sections resources from resourceMap
        for (const section of sections) {
            if (!section.assignedResources || !section.assignedResources.length) {
                continue;
            }
            for (const { type, id } of section.assignedResources) {
                const resourceGroup = resourceMap[type];
                if (resourceGroup && resourceGroup.resources && resourceGroup.resources[id]) {
                    delete resourceGroup.resources[id];
                }
            }
        }
        // Assign all remained resources
        for (const [type, resourceGroup] of resourceGroups) {
            if (resourceGroup && resourceGroup.resources && RESOURCE_TO_SECTION.indexOf(type) !== -1) {
                for (const id of Object.keys(resourceGroup.resources)) {
                    fallback.assignedResources.push({ type, id });
                }
            }
        }
        return config;
    }
}

export { createDefaultSections, DEFAULT_SECTION_ID };
